const en = {
  // Navigation
  nav: {
    features: 'Features',
    useCases: 'Use Cases',
    pricing: 'Pricing',
    blog: 'Blog',
    about: 'About',
    consulting: 'Consulting',
    login: 'Log in',
    startFree: 'Start Free →',
    dashboard: 'Dashboard',
    teams: 'Teams',
    tasks: 'Tasks',
    kpis: 'KPIs',
    users: 'Users',
    profileSettings: 'Profile Settings',
    companySettings: 'Company Settings',
    subscription: 'Subscription',
    superAdminPanel: 'SuperAdmin Panel',
    logout: 'Log out',
  },

  // Use cases dropdown
  useCases: {
    smallStartups: 'Small Startups (1-20)',
    smb: 'SMB (21-200)',
    enterprise: 'Enterprise',
    itTeams: 'IT Teams',
    ecommerce: 'E-commerce',
    saas: 'SaaS Companies',
    sales: 'Sales',
    operations: 'Operations',
    managementBoard: 'Management Board',
    publicInstitutions: 'Public Sector',
    nonprofits: 'Nonprofits',
    consulting: 'Consulting & Agencies',
    manufacturing: 'Manufacturing',
  },

  // Feature pages dropdown
  featurePages: {
    executionManagement: 'Execution Management',
    kpiManagement: 'KPI Management',
    taskManagement: 'Task Management',
    projectManagement: 'Project Management',
  },

  // Landing page
  landing: {
    badge: 'Outsprinter is free project management software',
    heroTitle1: 'Turn Strategy Into',
    heroTitle2: 'Execution',
    heroDescription: 'Align goals, KPIs, task and project management in one system. See progress, spot risks early and keep every team focused on what matters most!',
    getStarted: 'Create account',
    viewDemo: 'View Demo',
    featuresTitle: 'Everything you need to track performance',
    featuresSubtitle: 'Built for modern companies who want visibility without complexity.',
    fromBlog: 'From Our Blog',
    blogTitle: 'Expert Insights on KPI Management',
    blogSubtitle: 'Learn proven strategies to build effective KPI systems and drive performance.',
    readArticle: 'Read article',
    viewAllArticles: 'View All Articles',
    ctaTitle: 'Ready to see your performance clearly?',
    ctaSubtitle: 'Start now! Use Outsprinter to stay on track and grow your business',
    startTrial: 'Create account',
  },

  // Features
  features: {
    dashboard: 'Dashboard',
    dashboardDesc: 'Real-time charts and performance overview that updates instantly as teams enter data.',
    taskManagement: 'Task Management',
    taskManagementDesc: 'Create, assign, and track tasks with priorities, deadlines, subtasks, and recurring rules.',
    projectManagement: 'Project Management',
    projectManagementDesc: 'Organize work into projects with health metrics, workload analysis, and progress tracking.',
    kpiManagement: 'KPI Management',
    kpiManagementDesc: 'Define, track, and visualize key performance indicators across your entire organization.',
    goalPlanner: 'Goal Planner',
    goalPlannerDesc: 'Set yearly targets, break them into weekly milestones, and track progress toward strategic goals.',
    userManagement: 'User Management',
    userManagementDesc: 'Manage teams with org structure, role-based access, and department-level permissions.',
    aiAssistant: 'AI Assistant',
    aiAssistantDesc: 'Chat to plan better KPIs, analyze performance, and get actionable insights on your data.',
    notifications: 'Notifications',
    notificationsDesc: 'Stay informed with real-time alerts for task updates, KPI changes, and team activity.',
    dataImportExport: 'Data Import / Export',
    dataImportExportDesc: 'Import KPI data from Excel and export reports in CSV format for external analysis.',
  },

  // Footer
  footer: {
    description: 'The modern way for CEOs and teams to track KPIs, spot risks early, and keep everyone aligned on what matters most.',
    product: 'Product',
    useCases: 'Use Cases',
    resources: 'Resources',
    company: 'Company',
    legal: 'Legal',
    features: 'Features',
    pricing: 'Pricing',
    updates: 'Updates',
    blog: 'Blog',
    helpCenter: 'Help Center',
    faq: 'FAQ',
    aboutUs: 'About Us',
    consultingAdvisory: 'Consulting & Advisory',
    contact: 'Contact',
    press: 'Press',
    affiliates: 'Affiliates',
    termsOfService: 'Terms of Service',
    privacyNotice: 'Privacy Notice',
    cookiePolicy: 'Cookie Policy',
    copyright: '© 2026 Outsprinter. All rights reserved.',
    uptime: '99.9% Uptime',
  },

  // Pricing page
  pricing: {
    tryFree: 'Use for free',
    manageSubscription: 'Manage your subscription',
    chooseYourPlan: 'Choose your plan',
    yourSubscription: 'Your Subscription',
    subscribedSubtitle: "You're currently subscribed. Upgrade or manage your plan below.",
    unsubscribedSubtitle: 'All plans include the full Outsprinter feature set. Choose based on your team size.',
    monthly: 'Monthly',
    yearly: 'Yearly',
    save30: 'Save 30% compared to monthly',
    month: 'month',
    year: 'year',
    planLimits: 'Plan Limits',
    allFeaturesIncluded: 'All Features Included',
    bestFor: '🎯 Best for:',
    startFree: 'Start free',
    currentPlan: 'Current Plan',
    switchPlan: 'Switch Plan',
    subscribeNow: 'Subscribe Now',
    processing: 'Processing...',
    yourPlan: 'Your Plan',
    popular: 'Popular',
    enterpriseLine: 'Need more than 75 users, SSO, or API access?',
    contactUs: 'Contact us',
    forEnterprise: 'for Enterprise pricing.',
    needHelp: 'Need help? Contact our support team.',
    allPlansInclude: 'Outsprinter is free for everyone. No credit card required.',
    redirecting: 'Redirecting to checkout',
    redirectingDesc: "You'll be redirected to complete your subscription.",
    checkoutError: 'Failed to start checkout. Please try again.',
    // Plan names & descriptions
    starter: 'STARTER',
    starterDesc: 'For small teams getting started',
    professional: 'PROFESSIONAL',
    professionalDesc: 'For growing companies',
    business: 'BUSINESS',
    businessDesc: 'For larger organizations',
    // Limits
    teams3: '3 teams',
    kpis20: '20 KPIs',
    users5: '5 users',
    teams10: '10 teams',
    kpis75: '75 KPIs',
    users25: '25 users',
    teamsUnlimited: 'Unlimited teams',
    kpisUnlimited: 'Unlimited KPIs',
    users75: '75 users',
    // Suitable
    smallBusinesses: 'Small businesses',
    agencies: 'Agencies',
    employees520: '5–20 employees',
    scalingStartups: 'Scaling startups',
    employees20100: '20–100 employees',
    multiDepartment: 'Multi-department',
    employees100: '100+ employees',
    leadershipTeams: 'Leadership teams',
    investorReporting: 'Investor reporting',
    // Shared features
    dashboard: 'Dashboard',
    taskManagement: 'Task management',
    projectManagement: 'Project management',
    kpiManagement: 'KPI management',
    goalPlanner: 'Goal planner',
    userManagementOrg: 'User management with org structure',
    aiAssistant: 'AI assistant',
    notifications: 'Notifications',
    dataImportExport: 'Data import / export',
  },

  // About page
  about: {
    title: 'About Outsprinter',
    heroDescription: 'Outsprinter is a performance management platform built for leaders who want',
    realClarity: 'real clarity',
    realAccountability: 'real accountability',
    realResults: 'real results',
    missionIntro: 'We created Outsprinter to solve a problem that exists in almost every company: goals are set, but progress is unclear, fragmented, and often based on gut feeling instead of real data.',
    missionTitle: 'Our mission is simple —',
    missionDesc: 'to help companies manage their performance in a transparent, data-driven and consistent way, so that every team knows:',
    missionPoint1: 'what matters,',
    missionPoint2: 'how they are performing,',
    missionPoint3: 'and what needs attention right now.',
    executionRhythm: 'Outsprinter turns strategy into a weekly execution rhythm.',
    replacesExcel: 'It replaces scattered Excel files, forgotten KPIs and blurred accountability with:',
    kpiTree: 'KPI management',
    weeklyTracking: 'Project management',
    autoStatus: 'Task management',
    clearDashboards: 'and clear overviews for leadership and teams.',
    resultStatement: 'The result is not just better reporting - but stronger focus, faster decisions and measurable outcomes.',
    whyTitle: 'Why Outsprinter Was Created',
    whyIntro: "Outsprinter wasn't born from theory or consulting frameworks. It was born from",
    realExperience: 'real operating experience',
    whyIntroEnd: 'inside growing companies.',
    founderExperience: 'As a founder and CEO, I experienced firsthand how quickly organizations lose focus when:',
    whyPoint1: 'data lives in different tools,',
    whyPoint2: 'KPIs are tracked irregularly,',
    whyPoint3: "teams don't see the bigger picture,",
    whyPoint4: 'and leadership reacts too late.',
    solutionNotMeetings: 'The solution was not more meetings.',
    solutionWas: 'The solution was a',
    simpleVisual: 'simple, visual and disciplined performance system',
    thatForces: 'that forces:',
    weeklyReflection: 'weekly reflection,',
    honestNumbers: 'honest numbers,',
    clearOwnership: 'and clear ownership.',
    builtToEnsure: 'Outsprinter was built to make sure that:',
    ensurePoint1: "strategy doesn't stay on slides,",
    ensurePoint2: "goals don't die in spreadsheets,",
    ensurePoint3: "and execution becomes part of the company's weekly heartbeat.",
    founder: 'Founder',
    connectLinkedIn: 'Connect on LinkedIn',
    founderBio: 'Arti Kütt is an entrepreneur who has built startups, led international technology and e-commerce companies, and actively invests as a local angel investor. His work focuses on building scalable businesses with clear execution and measurable results.',
    philosophyTitle: 'Our Philosophy',
    belief1: 'What gets measured weekly, gets improved.',
    belief2: 'What is transparent, gets owned.',
    belief3: 'What is visual, gets understood.',
    belief4: 'What is tracked consistently, delivers results.',
    philosophyStatement: "We are building Outsprinter for founders, CEOs and leadership teams who don't want excuses, chaos, or delayed reactions — but want",
    controlClarityResults: 'control, clarity and results',
    visionTitle: 'Our Vision',
    visionStatement: 'To become the most simple and trusted performance operating system for leadership teams across Europe and beyond — where',
    strategyExecution: 'strategy, execution and accountability',
    liveInOnePlace: 'live in one place.',
  },

  // Auth page
  auth: {
    welcomeBack: 'Welcome back',
    createAccount: 'Create your account',
    loginDesc: 'Enter your credentials to access your dashboard',
    signupDesc: 'Get started with Outsprinter in seconds',
    inviteDesc: 'Complete your account setup to join your team',
    continueWithGoogle: 'Continue with Google',
    orContinueWith: 'Or continue with',
    fullName: 'Full Name',
    email: 'Email',
    password: 'Password',
    agreeToTerms: 'I agree to the',
    termsOfService: 'Terms of Service',
    and: 'and',
    privacyPolicy: 'Privacy Policy',
    forgotPassword: 'Forgot password?',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    createAccountBtn: 'Create account',
    creatingAccount: 'Creating account...',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    signUp: 'Sign up',
    resetPassword: 'Reset your password',
    resetDesc: "Enter your email address and we'll send you a link to reset your password.",
    resetSent: 'Check your email for a password reset link.',
    resetSentTo: "We've sent a password reset link to",
    checkSpam: "Didn't receive it? Check your spam folder or try again.",
    tryDifferentEmail: 'Try different email',
    done: 'Done',
    emailAddress: 'Email address',
    sendResetLink: 'Send Reset Link',
    sendingReset: 'Sending...',
  },

  // Contact page
  contact: {
    title: 'Contact Outsprinter',
    subtitle: "We're here to help you execute better.",
    description: "Whether you're evaluating Outsprinter, need support, or want to explore how it fits your organization, our team is ready to help.",
    getInTouch: '📩 Get in Touch',
    formIntro: 'Use the contact form to reach us regarding:',
    productQuestions: 'Product questions',
    salesInquiries: 'Sales inquiries',
    enterprisePricing: 'Enterprise pricing',
    onboardingTraining: 'Onboarding & training',
    supportRequests: 'Support requests',
    partnerships: 'Partnerships',
    name: 'Name',
    yourName: 'Your name',
    email: 'Email',
    yourEmail: 'your@email.com',
    company: 'Company',
    yourCompany: 'Your company',
    message: 'Message',
    howCanWeHelp: 'How can we help?',
    sendMessage: 'Send Message',
    responseTime: 'We typically respond within one business day.',
    salesDemos: '🧭 Sales & Demos',
    salesDesc: 'Interested in seeing Outsprinter in action or discussing pricing?',
    bookDemo: 'Book a Demo',
    bookLiveDemo: 'Book a live demo',
    requestEnterprise: 'Request enterprise pricing',
    customerSupport: '🛠️ Customer Support',
    supportDesc: 'Already a customer and need help?',
    inAppSupport: 'In-app support',
    emailSupportLabel: 'Email support',
    helpCenterResources: 'Help Center resources',
    visitHelpCenter: 'Visit Help Center',
    generalInquiries: '📧 General Inquiries',
    generalDesc: 'For partnerships, media, or general questions:',
    responseWithin24h: 'Response time: within 24 hours',
    ourApproach: '🌍 Our Approach',
    supportShouldBe: 'We believe support should be:',
    fast: 'Fast',
    clear: 'Clear',
    human: 'Human',
    focusedOnOutcomes: 'Focused on outcomes',
    noTicketBlackHoles: 'No ticket black holes. No unnecessary back-and-forth.',
    letsTalk: "📣 Let's talk about execution.",
    contactUsBtn: 'Contact Us',
  },

  // FAQ page
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Clear answers to common questions about Outsprinter.',
    stillHaveQuestions: "📣 Still have questions? We're here to help.",
    contactSupport: 'Contact Support',
    bookDemo: 'Book a Demo',
    // Categories
    productFunctionality: '🧠 Product & Functionality',
    kpisSprints: '📊 KPIs & Sprints',
    aiAssistant: '🧠 AI Assistant',
    usersAccounts: '👥 Users & Accounts',
    pricingBilling: '💳 Pricing & Billing',
    securityPrivacy: '🔐 Security & Privacy',
    support: '🛠️ Support',
    // Q&A - Product
    whatIsOutsprinter: 'What is Outsprinter?',
    whatIsOutsprinterA: 'Outsprinter is a B2B execution platform that helps companies set KPIs, plan weekly sprints, track execution, and gain AI-powered insights — all in one place.',
    isProjectMgmt: 'Is Outsprinter a project management tool?',
    isProjectMgmtA: 'No. Outsprinter focuses on execution, outcomes, and KPIs. It complements project tools like Jira or Asana rather than replacing them.',
    whatMakesDifferent: 'What makes Outsprinter different?',
    whatMakesDifferentA: 'Outsprinter connects strategy, KPIs, weekly cadence, sprints, and AI insights into one execution operating system.',
    // Q&A - KPIs
    howOftenUpdate: 'How often should KPIs be updated?',
    howOftenUpdateA: 'Outsprinter is built around a weekly cadence. Weekly updates provide fast feedback and better execution discipline.',
    howManyKpis: 'How many KPIs should we track?',
    howManyKpisA: 'Most teams perform best with 3–7 KPIs per team or department.',
    canKpisRollUp: 'Can KPIs roll up to company-level goals?',
    canKpisRollUpA: 'Yes. The KPI Tree structure allows full roll-up visibility.',
    // Q&A - AI
    whatDoesAiDo: 'What does the AI Assistant do?',
    whatDoesAiDoA: 'The AI Assistant analyzes KPIs and sprints, detects risks, generates summaries, and suggests improvements.',
    doesAiDecide: 'Does AI make decisions automatically?',
    doesAiDecideA: 'No. AI provides insights and recommendations — humans stay in control.',
    isDataUsedForAi: 'Is our data used to train public AI models?',
    isDataUsedForAiA: 'No. Customer data is isolated and never used for public model training.',
    // Q&A - Users
    canInviteExternal: 'Can we invite external users?',
    canInviteExternalA: 'Yes, admins can invite users and assign roles.',
    multipleCompanies: 'Can users belong to multiple companies?',
    multipleCompaniesA: 'Each user belongs to one company at a time for data isolation and security.',
    roleBasedSupported: 'Is role-based access supported?',
    roleBasedSupportedA: 'Yes. Outsprinter supports admin, manager, user, and viewer roles.',
    // Q&A - Pricing
    isFreeTrial: 'Is Outsprinter really free?',
    isFreeTrialA: 'Yes. Outsprinter is completely free for everyone — all features included, no credit card required.',
    canChangePlans: 'Are there any paid plans?',
    canChangePlansA: 'No. Outsprinter is free for everyone.',
    nonprofitDiscounts: 'Do nonprofits get a discount?',
    nonprofitDiscountsA: 'Outsprinter is free for nonprofits and everyone else.',
    // Q&A - Security
    isGdpr: 'Is Outsprinter GDPR compliant?',
    isGdprA: 'Yes. Outsprinter is designed with GDPR principles in mind.',
    whereDataStored: 'Where is data stored?',
    whereDataStoredA: 'Data is stored securely in cloud infrastructure with encryption at rest and in transit.',
    canExportDelete: 'Can we export or delete our data?',
    canExportDeleteA: 'Yes. Admins can export data and request full deletion.',
    // Q&A - Support
    whatSupportOptions: 'What support options are available?',
    whatSupportOptionsA: 'Email support, in-app help, onboarding sessions, and enterprise support options.',
    offerOnboarding: 'Do you offer onboarding?',
    offerOnboardingA: 'Yes. Guided onboarding is available for teams and enterprises.',
  },

  // Help Center page
  helpCenter: {
    title: 'Help Center',
    subtitle: 'Everything you need to get value from Outsprinter — fast.',
    description: "Whether you're a CEO, team lead, or individual contributor, you'll find clear guidance, step-by-step instructions, and practical examples.",
    structureTitle: '🔍 How the Help Center Is Structured',
    needMoreHelp: '📣 Need More Help?',
    cantFind: "If you can't find what you're looking for:",
    contactFromApp: 'Contact support directly from the app',
    emailSupport: 'Email our support team',
    bookOnboarding: 'Book a guided onboarding session',
    contactSupportBtn: 'Contact Support',
    bookOnboardingBtn: 'Book Onboarding Session',
    // Categories
    gettingStartedTitle: 'Getting Started',
    gettingStartedDesc: 'Perfect for new users and new teams.',
    kpiGuidesTitle: 'KPI Manager Guides',
    kpiGuidesDesc: 'Learn how to design and track meaningful KPIs.',
    sprintManagerTitle: 'Team Sprint Manager',
    sprintManagerDesc: 'Master weekly execution and sprint planning.',
    aiAssistantTitle: 'Smart AI Assistant',
    aiAssistantDesc: 'Get the most out of AI-powered insights.',
    dashboardsTitle: 'Dashboards & Reporting',
    dashboardsDesc: 'Learn how to interpret and use dashboards.',
    userCompanyMgmtTitle: 'User & Company Management',
    userCompanyMgmtDesc: 'Admin-level configuration and control.',
    securityTitle: 'Security, Privacy & Compliance',
    securityDesc: 'Understand how Outsprinter protects your data.',
    troubleshootingTitle: 'Troubleshooting & Support',
    troubleshootingDesc: 'Quick answers to common issues.',
    // Topics
    gs1: 'Creating your account and company',
    gs2: 'Inviting users and assigning roles',
    gs3: 'Setting up your first KPIs',
    gs4: 'Understanding the KPI Tree',
    gs5: 'Planning your first weekly sprint',
    kpi1: 'How to define effective KPIs',
    kpi2: 'Company vs. department KPIs',
    kpi3: 'Weekly KPI update best practices',
    kpi4: 'KPI thresholds (green / yellow / red)',
    kpi5: 'KPI ownership and accountability',
    kpi6: 'KPI trends and forecasting',
    sp1: 'How to plan weekly sprints',
    sp2: 'Choosing sprint priorities',
    sp3: 'Sprint reviews and retrospectives',
    sp4: 'Avoiding overcommitment',
    sp5: 'Measuring sprint success',
    ai1: 'How AI generates insights',
    ai2: 'AI-suggested KPIs and sprints',
    ai3: 'Understanding AI risk alerts',
    ai4: 'AI summaries and reports',
    ai5: 'How AI improves over time',
    dash1: 'Company-wide dashboards',
    dash2: 'Team dashboards',
    dash3: 'Board-level views',
    dash4: 'Forecast and trend analysis',
    dash5: 'Exporting reports (PDF, CSV)',
    ucm1: 'Managing users and roles',
    ucm2: 'Company profile settings',
    ucm3: 'Permissions and access control',
    ucm4: 'Billing and subscription management',
    ucm5: 'Audit logs',
    sec1: 'Data security overview',
    sec2: 'GDPR compliance',
    sec3: 'Access logging and audit trails',
    sec4: 'Data export and deletion',
    ts1: 'Login and access issues',
    ts2: 'KPI update problems',
    ts3: 'Sprint visibility issues',
    ts4: 'Notification troubleshooting',
  },

  // Use case shared
  useCaseCta: {
    startFreeTrial: 'Create account →',
    bookDemo: 'Book a Demo',
  },

  // Related links
  relatedLinks: {
    exploreFeatures: 'Explore Platform Features',
    exploreUseCases: 'Explore Use Cases',
    moreUseCases: 'More Use Cases',
    moreFeatures: 'More Features',
  },

  // Feature Page - Execution Management
  fpExec: {
    seoTitle: 'Execution Management Software | KPI, Tasks & Projects in One System',
    seoDesc: 'All-in-one execution management software to align strategy, KPIs, tasks, and projects. Gain full visibility and improve company performance.',
    badge: 'Execution Management',
    heroTitle: 'Execution Management Software',
    heroSubtitle: 'Turn strategy into measurable execution.',
    heroDesc: "Most companies don't fail because of bad strategy — they fail because execution is fragmented. Outsprinter is an execution management platform that unifies goals, KPIs, projects, and tasks into a single system — giving leadership and teams a shared source of truth.",
    startTrial: 'Create account',
    whatTitle: 'What Is Execution Management?',
    whatIntro: 'Execution management ensures that:',
    what1: 'Strategic goals are clearly defined',
    what2: 'Progress is measurable through KPIs',
    what3: 'Execution is driven through tasks and projects',
    what4: 'Leadership has real-time visibility',
    whatConclusion: 'It connects Planning → Execution → Results.',
    whyBreaksTitle: 'Why Execution Breaks Down',
    breaks1: 'KPIs tracked but not connected to work',
    breaks2: 'Tasks completed without impact',
    breaks3: 'Projects run without visibility',
    breaks4: 'Data scattered across tools',
    breaks5: 'Leadership sees results too late',
    howTitle: 'How Outsprinter Solves This',
    howIntro: 'Outsprinter creates a continuous execution loop:',
    howLoop: 'Goals → KPIs → Projects → Tasks → Insights → Decisions',
    howLoopDesc: 'Everything is connected and updated in real time.',
    coreTitle: 'Core Features of Execution Management',
    sec1Title: 'KPI Management (Performance Layer)',
    sec1Desc: 'Track performance at every level of the organization.',
    sec1a: 'Real-time KPI dashboards',
    sec1b: 'Weekly performance tracking',
    sec1c: 'KPI ownership and accountability',
    sec1d: 'Visual status indicators (green / yellow / red)',
    sec1e: 'KPI hierarchy (company → department → team)',
    sec1f: 'Historical trends and performance comparison',
    sec1Result: 'Leadership always knows how the business is performing.',
    sec2Title: 'Task Management (Execution Layer)',
    sec2Desc: 'Turn strategy into actionable work.',
    sec2a: 'Task creation with priorities and deadlines',
    sec2b: 'Subtasks (multi-level for complex workflows)',
    sec2c: 'Recurring tasks for operational routines',
    sec2d: 'Task duplication across multiple users',
    sec2e: 'Clear ownership and accountability',
    sec2f: 'Real-time status updates',
    sec2g: 'Notifications for changes and deadlines',
    sec2Result: 'Nothing falls through the cracks.',
    sec3Title: 'Project Management (Structure Layer)',
    sec3Desc: 'Organize work into measurable initiatives.',
    sec3a: 'Project-based work organization',
    sec3b: 'Project health tracking',
    sec3c: 'Progress tracking across tasks',
    sec3d: 'Workload and capacity visibility',
    sec3e: 'Cross-team collaboration',
    sec3f: 'Timeline and milestone tracking',
    sec3Result: 'Projects become predictable and transparent.',
    sec4Title: 'Goal Planner (Strategic Layer)',
    sec4Desc: 'Translate long-term strategy into execution.',
    sec4a: 'Define yearly or quarterly goals',
    sec4b: 'Break goals into measurable KPIs',
    sec4c: 'Track progress continuously',
    sec4d: 'Align all work to strategic priorities',
    sec4Result: 'Strategy becomes actionable.',
    sec5Title: 'Dashboards (Visibility Layer)',
    sec5Desc: 'Full real-time visibility across the company.',
    sec5a: 'Company-level dashboards',
    sec5b: 'Department performance views',
    sec5c: 'Project and task visibility',
    sec5d: 'Instant updates as data changes',
    sec5e: 'Drill-down capability',
    sec5Result: 'No more waiting for reports.',
    sec6Title: 'AI Assistant (Insight Layer)',
    sec6Desc: 'Turn data into actionable decisions.',
    sec6a: 'Analyze KPI trends',
    sec6b: 'Detect risks and anomalies',
    sec6c: 'Suggest improvements',
    sec6d: 'Help define better KPIs',
    sec6e: 'Provide performance summaries',
    sec6Result: 'Faster, smarter decision-making.',
    result: 'Result',
    add1Title: 'User & Organization Management',
    add1Desc: 'Control structure and access.',
    add1a: 'Role-based permissions',
    add1b: 'Department-level visibility',
    add1c: 'Organizational hierarchy',
    add1d: 'Secure data separation',
    add2Title: 'Notifications & Alerts',
    add2Desc: 'Stay informed without micromanagement.',
    add2a: 'Task updates',
    add2b: 'KPI changes',
    add2c: 'Project progress alerts',
    add3Title: 'Data Import & Export',
    add3Desc: 'Integrate with existing workflows.',
    add3a: 'Import KPI data from Excel',
    add3b: 'Export reports in CSV',
    add3c: 'Use data externally',
    benefitsTitle: 'Benefits of Execution Management',
    ben1: 'Complete visibility',
    ben2: 'Faster decisions',
    ben3: 'Clear accountability',
    ben4: 'Better alignment',
    ben5: 'Reduced complexity',
    featuresLink: 'Features overview',
    pricingLink: 'Pricing',
    ctaTitle: 'Ready to turn strategy into execution?',
    ctaDesc: 'Get started for free and see results in the first week.',
  },

  // Feature Page - KPI Management
  fpKPI: {
    seoTitle: 'KPI Management Software | Real-Time KPI Tracking & Dashboards',
    seoDesc: 'Track, analyze, and act on KPIs in real time. Connect metrics to execution with Outsprinter.',
    badge: 'KPI Management',
    heroTitle: 'KPI Management Software',
    heroSubtitle: 'Track performance. Drive decisions. Improve results.',
    heroDesc: 'Most KPI tools stop at dashboards. Outsprinter goes further — it connects KPIs directly to execution.',
    startTrial: 'Create account',
    featuresTitle: 'Advanced KPI Features',
    feat1Title: 'Real-Time KPI Tracking',
    feat1a: 'Instant updates',
    feat1b: 'Weekly tracking cadence',
    feat1c: 'No outdated reports',
    feat2Title: 'KPI Hierarchy (KPI Tree)',
    feat2a: 'Company → department → team KPIs',
    feat2b: 'Full alignment visibility',
    feat3Title: 'KPI Ownership',
    feat3a: 'Assign responsibility',
    feat3b: 'Clear accountability',
    feat4Title: 'KPI Status System',
    feat4a: 'Green / yellow / red indicators',
    feat4b: 'Immediate visibility of issues',
    feat5Title: 'Trend & Historical Analysis',
    feat5a: 'Track performance over time',
    feat5b: 'Compare periods',
    feat6Title: 'KPI → Task & Project Connection',
    feat6a: 'Link KPIs directly to execution',
    feat6b: 'Act immediately on performance changes',
    feat7Title: 'AI-Powered Insights',
    feat7a: 'Detect anomalies',
    feat7b: 'Highlight risks',
    feat7c: 'Suggest improvements',
    blogLink: 'Read blog insights',
    ctaTitle: 'Ready to take control of your KPIs?',
    ctaDesc: 'Start tracking performance in real time.',
  },

  // Feature Page - Task Management
  fpTask: {
    seoTitle: 'Advanced Task Management Software | Tasks, Subtasks & Recurring Work',
    seoDesc: 'Manage tasks with full structure: subtasks, recurring tasks, priorities, and deadlines. Improve execution and accountability.',
    badge: 'Task Management',
    heroTitle: 'Task Management Software',
    heroSubtitle: 'Structure work. Improve execution. Deliver results.',
    heroDesc: 'Outsprinter is not just a task tool — it connects tasks to outcomes.',
    startTrial: 'Create account',
    featuresTitle: 'Advanced Task Features',
    feat1Title: 'Task Assignment',
    feat1a: 'Assign to individuals',
    feat1b: 'Clear ownership',
    feat2Title: 'Subtasks (Multi-Level)',
    feat2a: 'Break down complex work',
    feat2b: 'Up to multiple layers',
    feat3Title: 'Recurring Tasks',
    feat3a: 'Automate routines',
    feat3b: 'Daily / weekly / monthly',
    feat4Title: 'Task Duplication',
    feat4a: 'Assign same task to multiple users',
    feat5Title: 'Priorities & Deadlines',
    feat5a: 'Focus on what matters',
    feat6Title: 'Task Visibility',
    feat6a: 'Across teams and departments',
    feat7Title: 'Notifications',
    feat7a: 'Real-time updates',
    outcomeTitle: 'Outcome',
    out1: 'Higher productivity',
    out2: 'Better accountability',
    out3: 'Structured execution',
    ctaTitle: "Ready to structure your team's execution?",
    ctaDesc: 'Get started for free today.',
  },

  // Feature Page - Project Management
  fpProject: {
    seoTitle: 'Project Management Software with KPI Tracking | Outsprinter',
    seoDesc: 'Manage projects, track progress, and align work with business goals. Full visibility in one system.',
    badge: 'Project Management',
    heroTitle: 'Project Management Software',
    heroSubtitle: 'Manage projects with clarity, control, and real impact.',
    startTrial: 'Create account',
    featuresTitle: 'Advanced Project Features',
    feat1Title: 'Project Structure',
    feat1a: 'Organize work into projects',
    feat1b: 'Clear ownership',
    feat2Title: 'Progress Tracking',
    feat2a: 'Real-time progress',
    feat2b: 'Linked tasks',
    feat3Title: 'Project Health Metrics',
    feat3a: 'Status indicators',
    feat3b: 'Risk visibility',
    feat4Title: 'Workload Management',
    feat4a: 'Balance team capacity',
    feat4b: 'Avoid overload',
    feat5Title: 'KPI Integration',
    feat5a: 'Link projects to outcomes',
    feat6Title: 'Cross-Team Collaboration',
    feat6a: 'Work across departments',
    consultingLink: 'Consulting support',
    ctaTitle: 'Ready to manage projects with impact?',
    ctaDesc: 'Get started for free and bring clarity to your projects.',
  },

  // Terms of Service
  terms: {
    title: 'Terms of Service',
    effectiveDate: 'Effective date',
    intro: 'Welcome to Outsprinter. These Terms of Service ("Terms") govern your access to and use of the Outsprinter platform, website, and services ("Service").',
    agreement: 'By creating an account or using the Service, you agree to be bound by these Terms.',
    s1Title: '1. Definitions',
    s1d1: 'refers to the company operating the Outsprinter platform.',
    s1d2: 'refers to the individual or entity using the Service.',
    s1d3: 'means a registered user account.',
    s1d4: 'means the organization created within Outsprinter.',
    s1d5: 'means all data, KPIs, text, and information entered into the Service.',
    s2Title: '2. Use of the Service',
    s2p1: 'You may use the Service only in accordance with these Terms and applicable laws.',
    s2agree: 'You agree to',
    s2a1: 'Provide accurate account information',
    s2a2: 'Maintain the security of your login credentials',
    s2a3: 'Use the Service only for lawful business purposes',
    s2mustNot: 'You must not',
    s2n1: 'Access data of another company',
    s2n2: 'Reverse engineer or misuse the platform',
    s2n3: 'Interfere with system security or availability',
    s3Title: '3. Accounts & Access',
    s3p1: 'Each user must have a unique account',
    s3p2: 'Users belong to a single company workspace',
    s3p3: 'Admins are responsible for managing access and permissions',
    s3p4: 'You are responsible for all activity under your account',
    s4Title: '4. Pricing',
    s4p1: 'Outsprinter is currently provided free of charge to all users',
    s4p2: 'No payment, credit card, or subscription is required to use the Service',
    s4p3: 'We reserve the right to introduce paid plans or features in the future with advance notice',
    s4p4: 'Reasonable usage limits may be applied to prevent abuse and ensure service stability',
    s4warning: 'Excessive automated or abusive usage may result in rate limiting or suspension.',
    s5Title: '5. Acceptable Use & Fair Usage',
    s5intro: 'To keep the Service free and reliable for everyone, you agree not to',
    s5p1: 'Use the Service for automated bulk operations beyond normal business use',
    s5p2: 'Create multiple accounts to circumvent any limits',
    s5p3: 'Resell, sublicense, or commercially redistribute access to the Service',
    s6Title: '6. Customer Content',
    s6p1: 'You retain ownership of all data and content you submit.',
    s6p2: 'You grant Outsprinter a limited license to',
    s6l1: 'Store',
    s6l2: 'Process',
    s6l3: 'Display',
    s6p3: 'your content solely for providing the Service.',
    s6noAi: 'We do not use customer data to train public AI models.',
    s7Title: '7. AI Features',
    s7p1: 'Outsprinter provides AI-generated insights and recommendations.',
    s7p2: 'You acknowledge',
    s7l1: 'AI outputs are informational only',
    s7l2: 'Final decisions remain your responsibility',
    s7l3: 'AI does not replace professional judgment',
    s8Title: '8. Data Protection & Privacy',
    s8p1: 'We process personal data in accordance with our Privacy Notice and applicable data protection laws, including GDPR.',
    s9Title: '9. Security',
    s9p1: 'We implement reasonable technical and organizational measures to protect your data. However, no system can guarantee absolute security.',
    s10Title: '10. Termination',
    s10p1: 'You may terminate your account at any time.',
    s10p2: 'We may suspend or terminate access if',
    s10l1: 'These Terms are violated',
    s10l2: 'Required by law',
    s10l3: 'Acceptable use policies are not met',
    s10p3: 'Upon termination, data may be deleted according to retention policies.',
    s11Title: '11. Limitation of Liability',
    s11intro: 'To the maximum extent permitted by law',
    s11l1: 'Outsprinter is not liable for indirect or consequential damages',
    s11l2: 'Total liability shall not exceed fees paid in the previous 12 months',
    s12Title: '12. Changes to Terms',
    s12p1: 'We may update these Terms from time to time. Continued use of the Service constitutes acceptance of the updated Terms.',
    s13Title: '13. Governing Law',
    s13p1: 'These Terms are governed by the laws of the European Union and applicable local jurisdiction.',
    s14Title: '14. Contact',
    s14p1: 'For questions regarding these Terms',
  },

  // Privacy Notice
  privacy: {
    title: 'Privacy Notice',
    effectiveDate: 'Effective date',
    intro: 'This Privacy Notice explains how Outsprinter collects, uses, and protects personal data.',
    s1Title: '1. Data Controller',
    s1p1: 'Outsprinter acts as the data controller for personal data processed through the Service.',
    s2Title: '2. Data We Collect',
    s2intro: 'We may collect',
    s2l1: 'Name',
    s2l2: 'Email address',
    s2l3: 'Company information',
    s2l4: 'Login and usage data',
    s2l5: 'Optional profile and organization details',
    s3Title: '3. How We Use Data',
    s3intro: 'We use personal data to',
    s3l1: 'Provide and operate the Service',
    s3l2: 'Authenticate users',
    s3l3: 'Send transactional and product communications',
    s3l4: 'Improve product functionality',
    s3l5: 'Provide customer support',
    s3l6: 'Meet legal obligations',
    s4Title: '4. Legal Basis for Processing',
    s4intro: 'We process data based on',
    s4l1: 'Contractual necessity',
    s4l2: 'Legitimate business interests',
    s4l3: 'Legal obligations',
    s4l4: 'User consent (where required)',
    s5Title: '5. Data Sharing',
    s5noSell: 'We do not sell personal data.',
    s5share: 'We may share data with',
    s5l1: 'Hosting and infrastructure providers',
    s5l2: 'Email delivery providers',
    s5l3: 'Analytics services',
    s5l4: 'Legal authorities when required',
    s5bound: 'All partners are contractually bound to protect data.',
    s6Title: '6. Data Retention',
    s6intro: 'We retain personal data only as long as necessary to',
    s6l1: 'Provide the Service',
    s6l2: 'Comply with legal requirements',
    s7Title: '7. Your Rights (GDPR)',
    s7intro: 'You have the right to',
    s7l1: 'Access your data',
    s7l2: 'Correct inaccurate data',
    s7l3: 'Request deletion',
    s7l4: 'Restrict or object to processing',
    s7l5: 'Export your data',
    s7request: 'Requests can be made via',
    s8Title: '8. Security Measures',
    s8intro: 'We use',
    s8l1: 'Encryption in transit and at rest',
    s8l2: 'Access controls',
    s8l3: 'Audit logging',
    s8l4: 'Secure cloud infrastructure',
    s9Title: '9. International Transfers',
    s9p1: 'If data is transferred outside the EU, appropriate safeguards are applied.',
    s10Title: '10. Changes',
    s10p1: 'We may update this Privacy Notice. Material changes will be communicated.',
    s11Title: '11. Contact',
    s11p1: 'For privacy-related questions',
  },

  // Cookie Policy
  cookie: {
    title: 'Cookie Policy',
    effectiveDate: 'Effective date',
    intro: 'This Cookie Policy explains how Outsprinter uses cookies and similar technologies.',
    s1Title: '1. What Are Cookies?',
    s1p1: 'Cookies are small text files stored on your device to improve website functionality and user experience.',
    s2Title: '2. Types of Cookies We Use',
    essential: 'Essential Cookies',
    essentialFor: 'Required for',
    e1: 'Authentication',
    e2: 'Security',
    e3: 'Core functionality',
    cannotDisable: 'These cannot be disabled.',
    analytics: 'Analytics Cookies',
    analyticsFor: 'Used to understand',
    a1: 'Platform usage',
    a2: 'Feature performance',
    a3: 'Traffic patterns',
    analyticsHelp: 'These help us improve the Service.',
    functional: 'Functional Cookies',
    functionalFor: 'Used to',
    f1: 'Remember preferences',
    f2: 'Improve usability',
    s3Title: '3. Managing Cookies',
    s3p1: 'You can manage or disable cookies through your browser settings.',
    s3note: 'Note: Disabling essential cookies may impact functionality.',
    s4Title: '4. Third-Party Cookies',
    s4p1: 'We may use trusted third-party tools (e.g. analytics providers). These providers comply with applicable data protection laws.',
    s5Title: '5. Consent',
    s5p1: 'Where required, cookies are used only after obtaining your consent via a cookie banner.',
    s6Title: '6. Updates',
    s6p1: 'This Cookie Policy may be updated from time to time.',
    s7Title: '7. Contact',
    s7p1: 'For cookie-related questions',
  },

  // Press page
  press: {
    title: 'Press',
    subtitle: 'Resources for journalists, media, and partners.',
    description: 'This page provides official information, brand assets, and contact details for press and media inquiries related to Outsprinter.',
    inquiriesTitle: '📰 Press Inquiries',
    inquiriesDesc: 'For media requests, interviews, or press coverage, please contact:',
    pressContact: 'Press Contact',
    responseTime: 'We aim to respond to press inquiries within 48 hours.',
    brandTitle: '🎨 Brand Assets',
    brandDesc: 'Available upon request:',
    asset1: 'Company logo (light & dark versions)',
    asset2: 'Product screenshots',
    asset3: 'Founder photos',
    asset4: 'Brand guidelines',
    brandNote: 'Please use approved assets only and do not alter logos or colors.',
    overviewTitle: '🏢 Company Overview (Boilerplate)',
    overviewP1: 'Outsprinter is a B2B execution platform that helps companies turn strategy into measurable results. The platform combines KPI management, weekly sprint execution, real-time dashboards, and AI-driven insights to help teams stay aligned, focused, and accountable.',
    overviewP2: 'Outsprinter is used by startups, scale-ups, enterprises, public institutions, and nonprofits to improve execution quality and decision-making.',
    topicsTitle: '🗞️ Topics We Comment On',
    topic1: 'Execution and performance management',
    topic2: 'KPI-driven leadership',
    topic3: 'Weekly cadence and sprint execution',
    topic4: 'AI in decision-making',
    topic5: 'Modern management practices',
    topic6: 'Scaling teams and organizations',
    ctaTitle: '📣 Covering execution, leadership, or SaaS innovation?',
    ctaButton: 'Contact Press Team',
  },

  // Affiliates page
  affiliates: {
    title: 'Affiliate Program',
    subtitle: 'Earn by helping teams execute better.',
    description: 'The Outsprinter Affiliate Program rewards partners who introduce companies to a better way of executing strategy, tracking KPIs, and running weekly sprints.',
    whoTitle: '🤝 Who the Program Is For',
    whoDesc: 'The affiliate program is ideal for:',
    audience1: 'Consultants & coaches',
    audience2: 'Fractional CEOs & COOs',
    audience3: 'Advisors & mentors',
    audience4: 'Agencies',
    audience5: 'Content creators',
    audience6: 'SaaS communities',
    whoNote: 'If you work with leadership teams, Outsprinter is a natural fit.',
    howTitle: '💰 How It Works',
    step1: 'You refer a company to Outsprinter',
    step2: 'They start a paid subscription',
    step3: 'You earn recurring commission',
    howNote: 'Simple, transparent, and scalable.',
    commTitle: '💸 Commission Model',
    comm1: 'Competitive recurring commission per paying customer',
    comm2: 'Long-term revenue share',
    comm3: 'No cap on earnings',
    comm4: 'Transparent tracking dashboard',
    commNote: 'Exact commission details are shared upon approval.',
    whyTitle: '🧩 Why Partner with Outsprinter',
    why1: 'High-value B2B product',
    why2: 'Clear value proposition',
    why3: 'Strong retention potential',
    why4: 'Growing demand for execution tools',
    why5: 'Dedicated partner support',
    whyNote: 'You help companies win — and get rewarded for it.',
    startTitle: '🚀 Getting Started',
    startDesc: 'To apply',
    startStep1: 'Fill out the affiliate application',
    startStep2: 'Get approved',
    startStep3: 'Receive your unique referral link',
    startStep4: 'Start sharing Outsprinter',
    applyBtn: 'Apply to Affiliate Program',
    ctaTitle: '📣 Help companies execute better — and grow with us.',
    ctaBtn: 'Become an Affiliate',
  },

  // Updates page
  updates: {
    title: 'Updates',
    badge: 'Product Updates',
    subtitle: 'Product improvements, new features, and continuous innovation — transparently shared.',
    introP1: 'Outsprinter is built as a continuously evolving execution platform. We ship improvements regularly to make KPI tracking, sprint execution, and AI-driven insights faster, smarter, and more reliable.',
    introP2: "This page keeps you informed about what's new, what's improved, and what's coming next.",
    latestTitle: 'Latest Updates',
    latestDesc: 'Stay current with every release. Each update focuses on real customer needs: better clarity, faster execution, and deeper insights.',
    latest1: 'Enhanced KPI Tree visualization for large organizations',
    latest2: 'Faster weekly sprint planning with AI-assisted prioritization',
    latest3: 'Improved dashboard performance and real-time refresh',
    latest4: 'New AI insights for execution risks and performance trends',
    latest5: 'Expanded admin controls for company and user management',
    latestNote: 'Every release is designed to reduce friction and improve decision-making.',
    releaseTitle: 'Release Notes',
    releaseDesc: 'Clear, structured, and transparent. Each release note includes:',
    whatsNew: "What's new",
    newFeatures: 'New features',
    newCapabilities: 'New capabilities',
    whatsImproved: "What's improved",
    perfEnhancements: 'Performance enhancements',
    uxImprovements: 'UX improvements',
    bugFixes: 'Bug fixes',
    securityUpdates: 'Security updates',
    stabilityFixes: 'Stability fixes',
    aiTitle: 'AI Feature Updates',
    aiDesc: "Smarter execution, every month. Outsprinter's AI Assistant is continuously trained and improved.",
    ai1: 'Smarter KPI suggestions based on company size and industry',
    ai2: 'Improved sprint forecasting accuracy',
    ai3: 'Better anomaly detection in KPI trends',
    ai4: 'More actionable leadership insights',
    ai5: 'Enhanced natural-language summaries for weekly reviews',
    aiGoal: 'Our goal: less manual analysis, more confident decisions.',
    betaTitle: 'Beta Program',
    betaDesc: "Get early access to what's next. Join the Outsprinter Beta Program to:",
    betaBenefitsTitle: 'Benefits',
    beta1: 'Test new features before public release',
    beta2: 'Influence product direction',
    beta3: 'Provide direct feedback to the product team',
    beta4: 'Gain early access to advanced AI capabilities',
    betaGetTitle: 'Beta users get',
    betaGet1: 'Early feature flags',
    betaGet2: 'Priority feedback channels',
    betaGet3: 'Early adopter recognition',
    betaNote: '👉 Available for Pro, Business, and Enterprise customers.',
    perfTitle: 'Performance & Stability Improvements',
    perfDesc: 'We continuously invest in:',
    perf1: 'Faster dashboard load times',
    perf2: 'Improved system reliability',
    perf3: 'Scalable infrastructure for growing teams',
    perf4: 'Enhanced data security and monitoring',
    perfNote: 'Every update is tested to ensure minimal disruption and maximum stability.',
    secTitle: 'Security & Compliance Updates',
    secDesc: 'Security is treated as a core product feature. Updates include:',
    sec1: 'Regular dependency updates',
    sec2: 'Infrastructure hardening',
    sec3: 'Improved access logging',
    sec4: 'GDPR and data protection enhancements',
    secNote: 'Enterprise and compliance-focused customers are notified of relevant changes proactively.',
    roadmapTitle: "What's Coming Next",
    roadmapDesc: 'Our near-term roadmap focuses on:',
    road1: 'Advanced AI forecasting for KPIs',
    road2: 'Cross-team dependency visualization',
    road3: 'Improved board-level reporting',
    road4: 'Deeper integrations (CRM, HR, BI tools)',
    road5: 'Custom KPI frameworks by industry',
    roadmapNote: 'We build with customer input — and always with execution quality in mind.',
    ctaTitle: 'Stay ahead with a platform that never stands still.',
  },

  // Consulting page
  consulting: {
    title: 'Consulting',
    badge: 'Advisory & Implementation',
    heroTitle: 'Turning Strategy into Real Execution',
    heroDesc: 'Many companies know what they want to achieve, but struggle with execution. Our consultants help you implement new internal processes that actually work in day-to-day management — not just in presentations or documents.',
    heroDesc2: 'We help you build a management system that creates',
    clarity: 'clarity',
    accountability: 'accountability',
    results: 'measurable business results',
    whatWeDoTitle: 'What We Do',
    whatWeDoDesc: 'Comprehensive consulting services to transform your strategy into executable reality',
    service1Title: 'Management & KPI Framework Design',
    service1Desc: 'We help you define clear objectives (OKRs, KPIs) across the entire organization — from company level to departments and teams. The result is a transparent structure where everyone understands how their work contributes to business outcomes.',
    service2Title: 'Process Design & Implementation',
    service2Desc: 'We analyze your current workflows and help design and roll out new processes that:',
    service2b1: 'Improve accountability and ownership',
    service2b2: 'Reduce manual work and ambiguity',
    service2b3: 'Create disciplined, repeatable execution',
    service3Title: 'Leadership & Team Enablement',
    service3Desc: 'Even the best processes fail if people don\'t use them. We work closely with leadership and department heads to:',
    service3b1: 'Align leaders around why the change matters',
    service3b2: 'Train managers to use KPIs in daily leadership',
    service3b3: 'Embed the system into the company\'s management culture',
    service4Title: 'Measurement, Reporting & Continuous Improvement',
    service4Desc: 'We establish clear operating rhythms (e.g. weekly KPI reviews), dashboards, and visual reports that make it immediately clear:',
    service4b1: 'What is working',
    service4b2: 'Where performance is off track',
    service4b3: 'Where leadership intervention is required',
    whoTitle: 'Who This Is For',
    whoDesc: 'Our consulting is designed for organizations ready to transform their execution',
    target1: 'Growing B2B companies',
    target2: 'Production, service, and technology businesses',
    target3: 'Leadership teams seeking clarity and control',
    target4: 'Organizations with a strategy in place but weak or slow execution',
    approachTitle: 'Our Approach',
    approach1: 'Practical, not theoretical — focused on real-world usage',
    approach2: 'System-driven — goals, processes, people, and tools are aligned',
    approach3: 'Measurable — every change must impact results',
    approach4: 'Collaborative — we implement together with your leadership team',
    outcomesTitle: 'Outcomes Clients Experience',
    outcome1: 'Full transparency across the organization',
    outcome2: 'Clearer accountability at every level',
    outcome3: 'Faster and better decision-making',
    outcome4: 'Stronger execution and predictable results',
    outcome5: 'KPIs that function as real management tools, not just numbers',
    engageTitle: 'How the Engagement Starts',
    engage1: 'Introductory consultation',
    engage2: 'Assessment of current management practices',
    engage3: 'Clear recommendations and action plan',
    engage4: 'Hands-on implementation with leadership and teams',
    ctaTitle: 'Ready to Transform Your Execution?',
    ctaDesc: "Let's discuss how we can help you build a management system that delivers real results.",
    ctaBtn1: 'Book a Free Consultation',
    ctaBtn2: "Let's Talk About Your Challenges",
  },

  // Blog page
  blog: {
    seoTitle: 'KPI Management Blog - Expert Insights & Best Practices',
    seoDesc: 'Learn about KPI management, performance tracking, and business leadership. Expert articles on building effective KPI systems, weekly tracking, and data-driven decision making.',
    badge: 'KPI Management Insights',
    title: 'The Outsprinter Blog',
    subtitle: 'Expert insights on KPI management, performance tracking, and building data-driven organizations.',
    searchPlaceholder: 'Search articles...',
    allArticles: 'All Articles',
    noResults: 'No articles found matching your search.',
    clearFilters: 'Clear filters',
    ctaTitle: 'Ready to Transform Your KPI Management?',
    ctaDesc: 'Join hundreds of companies using Outsprinter to track performance weekly and make better decisions.',
    ctaBtn: 'Create account',
  },

  // Common
  common: {
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    noResults: 'No results found',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    and: 'and',
  },

  // Use Case - Small Startups
  ucStartups: {
    seoTitle: 'Startup Execution Software | KPI, Task & Project Management',
    seoDesc: 'All-in-one execution platform for startups. Manage KPIs, tasks, and projects in one system. Stay focused, aligned, and grow faster.',
    badge: 'Small Startups (1–20 Employees)',
    heroTitle: 'Build focus early. Execute fast. Grow with clarity.',
    heroDesc: 'Startups move fast — but without structure, speed quickly turns into chaos. Founders juggle priorities, teams react instead of plan, and progress is often based on intuition rather than data. Outsprinter is a startup execution platform that connects goals, KPIs, tasks, and projects into one system — helping early teams stay focused and execute consistently.',
    challengeTitle: 'The Challenge for Startups',
    challengeIntro: 'Startups typically struggle with:',
    ch1: 'Too many ideas, not enough focus',
    ch2: 'No clear connection between strategy and daily work',
    ch3: 'KPIs not tracked or updated consistently',
    ch4: 'Tasks scattered across tools (Notion, Slack, Excel)',
    ch5: 'Founders lacking real-time visibility',
    challengeConclusion: 'As a result, teams stay busy — but not always effective.',
    howTitle: 'How Outsprinter Helps Startups Execute Better',
    howIntro: 'Outsprinter creates a simple but powerful execution system:',
    how1: 'Define company direction with the Goal Planner',
    how2: 'Track progress through real-time KPIs',
    how3: 'Turn plans into action using tasks and projects',
    how4: 'Use AI insights to improve performance',
    howConclusion: 'Execution becomes structured without slowing the team down.',
    workflowTitle: 'Typical Startup Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Founder defines company goals and KPIs',
    wf1b: 'Creates initial projects (growth, product, sales)',
    wf1c: 'Assigns ownership across the team',
    wf2Title: 'Daily Execution',
    wf2a: 'Tasks tracked with deadlines and priorities',
    wf2b: 'Subtasks used to break down complex work',
    wf2c: 'Recurring tasks for routines (reports, standups, ops)',
    wf3Title: 'Weekly Overview',
    wf3a: 'KPIs updated',
    wf3b: 'Dashboard shows company performance',
    wf3c: 'AI highlights risks and opportunities',
    featuresTitle: 'Key Features for Startups',
    feat1Title: 'KPI Management',
    feat1a: 'Track key metrics in one system',
    feat1b: 'Visual performance indicators',
    feat1c: 'Weekly progress tracking',
    feat2Title: 'Task Management',
    feat2a: 'Assign tasks with clear ownership',
    feat2b: 'Priorities, deadlines, subtasks',
    feat2c: 'Recurring workflows',
    feat3Title: 'Project Management',
    feat3a: 'Structure work into initiatives',
    feat3b: 'Track progress and accountability',
    feat4Title: 'Dashboards',
    feat4a: 'Real-time company overview',
    feat4b: 'Instant visibility into performance',
    feat5Title: 'AI Assistant',
    feat5a: 'KPI suggestions',
    feat5b: 'Performance insights',
    feat5c: 'Risk detection',
    resultsTitle: 'Measurable Outcomes',
    resultsIntro: 'Startups using Outsprinter typically achieve:',
    res1: 'Faster execution',
    res2: 'Clearer priorities',
    res3: 'Better team alignment',
    res4: 'Reduced chaos',
    res5: 'Data-driven decisions',
    whyTitle: 'Why Outsprinter Fits Startups',
    why1: 'Fast setup (under 30 minutes)',
    why2: 'No complex configuration',
    why3: 'Combines multiple tools into one',
    why4: 'Scales with the company',
    ctaTitle: 'Build execution discipline from the start.',
  },

  // Use Case - SMB
  ucSMB: {
    seoTitle: 'Execution Software for Growing Companies | KPI & Project Management',
    seoDesc: 'Align teams, track KPIs, and manage projects in one system. Outsprinter helps SMBs scale execution without losing control.',
    badge: 'Small & Medium Companies (21–200 Employees)',
    heroTitle: 'Create alignment across teams. Execute at scale. Stay in control.',
    heroDesc: 'As companies grow, execution becomes harder — not easier. More teams, more managers, and more priorities create misalignment and slow decision-making. Outsprinter is an execution management platform for growing companies that connects KPIs, tasks, and projects into one unified system.',
    challengeTitle: 'The Challenge for Growing Companies',
    challengeIntro: 'Growing companies typically struggle with:',
    ch1: 'Teams working in silos',
    ch2: 'Department goals not aligned with company strategy',
    ch3: 'KPIs tracked inconsistently or too late',
    ch4: 'Work happening across multiple tools',
    ch5: 'Leadership reacting instead of leading',
    challengeConclusion: 'Growth exposes execution gaps.',
    howTitle: 'How Outsprinter Brings Structure Without Slowing You Down',
    howIntro: "Outsprinter introduces structure where it's needed — and flexibility where it matters. With Outsprinter, SMBs can:",
    how1: 'Align company goals with department KPIs',
    how2: 'Connect KPIs directly to projects and tasks',
    how3: 'Track execution in real time',
    how4: 'Identify risks early using AI',
    how5: 'Maintain speed while scaling',
    howConclusion: '',
    workflowTitle: 'Typical SMB Workflow in Outsprinter',
    wf1Title: 'Company Setup',
    wf1a: 'Leadership defines company-level KPIs',
    wf1b: 'Departments create aligned KPIs',
    wf1c: 'Projects created to drive key initiatives',
    wf1d: 'Ownership assigned across the organization',
    wf2Title: 'Execution',
    wf2a: 'Teams manage tasks and projects',
    wf2b: 'Recurring processes tracked (reports, operations)',
    wf2c: 'Work visible across departments',
    wf3Title: 'Leadership Review',
    wf3a: 'CEO reviews company dashboard',
    wf3b: 'Red KPIs trigger focused discussions',
    wf3c: 'AI highlights risks and trends',
    featuresTitle: 'Key Features for SMBs',
    feat1Title: 'KPI Tree',
    feat1a: 'Company → department → KPI structure',
    feat1b: 'Full alignment visibility',
    feat2Title: 'Task Management',
    feat2a: 'Clear ownership across teams',
    feat2b: 'Priorities, deadlines, subtasks',
    feat3Title: 'Project Management',
    feat3a: 'Track initiatives',
    feat3b: 'Monitor workload and progress',
    feat4Title: 'Dashboards',
    feat4a: 'Company-wide overview',
    feat4b: 'Department performance insights',
    feat5Title: 'AI Assistant',
    feat5a: 'Risk detection',
    feat5b: 'Performance insights',
    resultsTitle: 'Measurable Outcomes',
    resultsIntro: 'Companies typically see:',
    res1: 'Stronger cross-team alignment',
    res2: 'Faster leadership decisions',
    res3: 'Fewer surprises in performance',
    res4: 'Clear ownership of results',
    res5: 'Higher execution consistency',
    whyTitle: 'Why Outsprinter Fits Growing Organizations',
    why1: 'Scales without re-implementation',
    why2: 'Works across multiple teams',
    why3: 'Combines KPIs, tasks, and projects',
    why4: 'Enables structured execution',
    ctaTitle: 'Scale execution without losing clarity.',
  },

  // Use Case - Enterprise
  ucEnterprise: {
    seoTitle: 'Enterprise Execution Platform | KPI, Project & Task Management',
    seoDesc: 'Enterprise execution platform for aligning strategy, KPIs, and projects. Gain real-time visibility and control across your organization.',
    badge: 'Enterprise',
    heroTitle: 'Bring clarity to complexity. Align execution across the entire organization.',
    heroDesc: "Large organizations don't struggle with strategy — they struggle with execution at scale. Multiple business units, complex structures, and fragmented systems create a lack of visibility and slow decision-making. Outsprinter is an enterprise execution platform that connects strategy, KPIs, projects, and tasks into one transparent system.",
    challengeTitle: 'The Challenge for Enterprises',
    challengeIntro: 'Enterprise organizations commonly face:',
    ch1: 'Disconnected business units',
    ch2: 'Delayed and filtered reporting',
    ch3: 'Multiple tools and fragmented data',
    ch4: 'Slow reaction to issues',
    ch5: 'Lack of real-time visibility',
    challengeConclusion: 'Execution becomes complex and opaque.',
    howTitle: 'How Outsprinter Solves Enterprise Execution',
    howIntro: 'Outsprinter provides a unified execution system:',
    how1: 'Align strategy with measurable KPIs',
    how2: 'Connect KPIs to projects and tasks',
    how3: 'Provide real-time dashboards for leadership',
    how4: 'Standardize execution across departments',
    howConclusion: '',
    workflowTitle: 'Typical Enterprise Workflow',
    wf1Title: 'Strategic Layer',
    wf1a: 'Define company-level KPIs',
    wf1b: 'Align business units',
    wf1c: 'Set long-term goals',
    wf2Title: 'Execution Layer',
    wf2a: 'Manage projects across departments',
    wf2b: 'Assign tasks with ownership',
    wf2c: 'Track progress continuously',
    wf3Title: 'Leadership Layer',
    wf3a: 'Executive dashboards',
    wf3b: 'Real-time performance insights',
    wf3c: 'AI-driven risk detection',
    featuresTitle: 'Key Features for Enterprise',
    feat1Title: 'KPI Framework',
    feat1a: 'Multi-level KPI structure',
    feat1b: 'Cross-unit visibility',
    feat2Title: 'Project Management',
    feat2a: 'Track large initiatives',
    feat2b: 'Monitor performance',
    feat3Title: 'Task Management',
    feat3a: 'Organization-wide execution tracking',
    feat3b: 'Clear ownership',
    feat4Title: 'Dashboards',
    feat4a: 'Board-level visibility',
    feat4b: 'Real-time data',
    feat5Title: 'AI Assistant',
    feat5a: 'Predictive insights',
    feat5b: 'Risk detection',
    resultsTitle: 'Measurable Outcomes',
    resultsIntro: 'Organizations typically achieve:',
    res1: 'Faster decision-making',
    res2: 'Increased transparency',
    res3: 'Better alignment',
    res4: 'Reduced reporting overhead',
    res5: 'Stronger execution control',
    whyTitle: 'Why Outsprinter Fits Enterprise',
    why1: 'Supports complex structures',
    why2: 'Scales across departments',
    why3: 'Flexible but structured',
    why4: 'Enterprise-level visibility',
    ctaTitle: 'Bring clarity to enterprise execution.',
  },

  // Use Case - IT Teams
  ucIT: {
    seoTitle: 'IT Execution Software | Task, Project & KPI Management',
    seoDesc: 'Align IT work with business goals. Manage tasks, projects, and KPIs in one system. Improve delivery, visibility, and performance.',
    badge: 'IT Teams',
    heroTitle: 'Turn technical work into measurable business impact.',
    heroDesc: "IT teams deliver critical work — but leadership often struggles to see the real impact. Tasks, tickets, and projects are tracked, but rarely connected to business outcomes. Outsprinter is an IT execution platform that connects technical work to measurable results through KPIs, projects, and structured task management.",
    challengeTitle: 'The Challenge for IT Teams',
    challengeIntro: 'IT teams often face:',
    ch1: 'Too many tasks and competing priorities',
    ch2: 'Work disconnected from business outcomes',
    ch3: 'Limited visibility for leadership',
    ch4: 'Overloaded teams and uneven workload',
    ch5: 'Constant firefighting instead of planned execution',
    challengeConclusion: '',
    howTitle: 'How Outsprinter Improves IT Execution',
    howIntro: 'With Outsprinter, IT teams can:',
    how1: 'Define technical KPIs (uptime, delivery speed, reliability)',
    how2: 'Connect projects to business objectives',
    how3: 'Manage tasks with clear ownership and deadlines',
    how4: 'Balance workload across the team',
    how5: 'Gain visibility into delivery and performance',
    howConclusion: '',
    workflowTitle: 'Typical IT Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define technical KPIs',
    wf1b: 'Create projects (features, infrastructure, improvements)',
    wf1c: 'Assign ownership',
    wf2Title: 'Execution',
    wf2a: 'Tasks tracked with subtasks and priorities',
    wf2b: 'Recurring tasks for maintenance and operations',
    wf2c: 'Projects monitored for progress',
    wf3Title: 'Leadership View',
    wf3a: 'Dashboards show delivery health',
    wf3b: 'AI highlights risks and bottlenecks',
    featuresTitle: 'Key Features for IT Teams',
    feat1Title: 'KPI Management',
    feat1a: 'Track uptime, performance, delivery metrics',
    feat2Title: 'Task Management',
    feat2a: 'Structured task execution',
    feat2b: 'Subtasks and recurring work',
    feat3Title: 'Project Management',
    feat3a: 'Feature and infrastructure tracking',
    feat3b: 'Progress visibility',
    feat4Title: 'Dashboards',
    feat4a: 'Delivery and performance overview',
    feat5Title: 'AI Assistant',
    feat5a: 'Bottleneck detection',
    feat5b: 'Performance insights',
    resultsTitle: 'Measurable Outcomes',
    resultsIntro: 'IT teams typically achieve:',
    res1: 'Better prioritization',
    res2: 'Reduced overload',
    res3: 'Improved delivery predictability',
    res4: 'Clear visibility for leadership',
    whyTitle: '',
    ctaTitle: 'Align technology with outcomes.',
  },

  // Use Case - E-commerce
  ucEcom: {
    seoTitle: 'E-commerce KPI & Performance Management Software',
    seoDesc: 'Track revenue, conversion, and marketing KPIs while managing tasks and campaigns. Scale your e-commerce business with clarity.',
    badge: 'E-commerce',
    heroTitle: 'Move faster. Optimize performance. Scale efficiently.',
    heroDesc: 'E-commerce companies operate in a fast-changing environment where performance shifts daily. Without a clear system, decisions are delayed and growth becomes unpredictable. Outsprinter is an e-commerce execution platform that connects performance metrics, campaigns, and execution into one system.',
    challengeTitle: 'The Challenge',
    challengeIntro: 'E-commerce companies typically face:',
    ch1: 'Data spread across tools (ads, analytics, CRM)',
    ch2: 'No single performance overview',
    ch3: 'Marketing and operations misaligned',
    ch4: 'Slow reaction to performance changes',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, e-commerce teams can:',
    how1: 'Track KPIs (conversion rate, revenue, CAC, AOV)',
    how2: 'Manage campaigns as projects',
    how3: 'Assign tasks across marketing and operations',
    how4: 'Monitor performance in real time',
    workflowTitle: 'Typical Workflow',
    wf1Title: 'Setup',
    wf1a: 'Define growth KPIs',
    wf1b: 'Create marketing and operational projects',
    wf2Title: 'Execution',
    wf2a: 'Tasks for campaigns and optimizations',
    wf2b: 'Recurring tasks for reporting',
    wf3Title: 'Monitoring',
    wf3a: 'Real-time dashboards',
    wf3b: 'AI highlights performance drops',
    featuresTitle: 'Key Features',
    feat1: 'KPI tracking for growth metrics',
    feat2: 'Project management for campaigns',
    feat3: 'Task management for execution',
    feat4: 'Real-time dashboards',
    feat5: 'AI insights',
    resultsTitle: 'Outcomes',
    res1: 'Faster optimization cycles',
    res2: 'Better decision-making',
    res3: 'Increased growth predictability',
    ctaTitle: 'Scale your e-commerce with clarity.',
  },

  // Use Case - SaaS
  ucSaaS: {
    seoTitle: 'SaaS KPI Tracking & Execution Platform',
    seoDesc: 'Track ARR, churn, and growth KPIs while managing projects and tasks. Align product, sales, and marketing teams.',
    badge: 'SaaS Companies',
    heroTitle: 'Scale with data-driven execution.',
    heroDesc: 'SaaS companies rely heavily on metrics — but those metrics are often disconnected from daily execution. Outsprinter is a SaaS execution platform that connects growth KPIs with projects and tasks.',
    challengeTitle: 'The Challenge',
    challengeIntro: 'SaaS companies typically face:',
    ch1: 'Metrics tracked but not acted upon',
    ch2: 'Misalignment between product, sales, and marketing',
    ch3: 'Slow iteration cycles',
    ch4: 'Lack of execution visibility',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, SaaS teams can:',
    how1: 'Track key SaaS metrics (ARR, churn, LTV)',
    how2: 'Manage growth initiatives as projects',
    how3: 'Align teams through shared KPIs',
    how4: 'Monitor execution in real time',
    workflowTitle: 'Typical Workflow',
    wf1: 'Define growth KPIs',
    wf2: 'Create projects (product, growth, sales)',
    wf3: 'Assign tasks',
    wf4: 'Track results',
    resultsTitle: 'Outcomes',
    res1: 'Predictable growth',
    res2: 'Faster iteration',
    res3: 'Better alignment',
    ctaTitle: 'Scale your SaaS with execution clarity.',
  },

  // Use Case - Sales
  ucSales: {
    seoTitle: 'Sales KPI & Pipeline Management Software',
    seoDesc: 'Track pipeline, revenue, and sales KPIs while managing tasks. Improve forecasting and close more deals.',
    badge: 'Sales Teams',
    heroTitle: 'Turn activity into predictable revenue.',
    heroDesc: "Sales teams are active — but activity alone doesn't guarantee results. Without visibility and structure, forecasting becomes unreliable. Outsprinter is a sales execution platform connecting KPIs, pipeline work, and tasks.",
    challengeTitle: 'The Challenge',
    challengeIntro: 'Sales teams typically face:',
    ch1: 'Poor pipeline visibility',
    ch2: 'Inaccurate forecasting',
    ch3: 'Activity without clear outcomes',
    ch4: 'Lack of alignment with company goals',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, sales teams can:',
    how1: 'Track sales KPIs (pipeline, conversion, revenue)',
    how2: 'Manage deal execution as tasks',
    how3: 'Align sales with company goals',
    how4: 'Monitor performance in real time',
    workflowTitle: 'Typical Workflow',
    wf1: 'Define revenue KPIs',
    wf2: 'Track pipeline performance',
    wf3: 'Assign deal-related tasks',
    wf4: 'Monitor results',
    resultsTitle: 'Outcomes',
    res1: 'Better forecasting',
    res2: 'Higher conversion rates',
    res3: 'Clear priorities',
    ctaTitle: 'Make revenue execution predictable.',
  },

  // Use Case - Operations
  ucOps: {
    seoTitle: 'Operations Management Software with KPIs & Tasks',
    seoDesc: 'Manage operational KPIs, recurring tasks, and improvement projects. Improve efficiency and reliability.',
    badge: 'Operational Teams',
    heroTitle: 'Run operations with clarity and consistency.',
    heroDesc: 'Operations teams ensure everything runs smoothly — but often lack structured systems and visibility. Outsprinter is an operations execution platform combining KPIs, tasks, and projects.',
    challengeTitle: 'The Challenge',
    challengeIntro: 'Operations teams typically face:',
    ch1: 'Manual processes',
    ch2: 'Lack of visibility',
    ch3: 'Reactive operations',
    ch4: 'Inconsistent execution',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, operations teams can:',
    how1: 'Track operational KPIs',
    how2: 'Manage recurring tasks',
    how3: 'Improve processes through projects',
    how4: 'Gain real-time visibility',
    workflowTitle: 'Typical Workflow',
    wf1: 'Define operational KPIs',
    wf2: 'Create recurring tasks',
    wf3: 'Manage improvement projects',
    resultsTitle: 'Outcomes',
    res1: 'Increased efficiency',
    res2: 'Better reliability',
    res3: 'Continuous improvement',
    ctaTitle: 'Run operations with confidence.',
  },

  // Use Case - Management Board
  ucBoard: {
    seoTitle: 'Executive Dashboard & KPI Management Platform',
    seoDesc: 'Real-time dashboards for executives. Track KPIs, projects, and execution across your company.',
    badge: 'Management Board',
    heroTitle: 'Make decisions based on real data — not reports.',
    heroDesc: 'Executives often rely on delayed, filtered reports. This slows down decision-making and reduces visibility. Outsprinter provides real-time executive dashboards connected to execution.',
    challengeTitle: 'The Challenge',
    challengeIntro: 'Management boards typically face:',
    ch1: 'Delayed reporting',
    ch2: 'Lack of transparency',
    ch3: 'Slow decisions',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, executives get:',
    how1: 'Real-time KPI dashboards',
    how2: 'Full visibility into execution',
    how3: 'AI insights for faster decisions',
    resultsTitle: 'Outcomes',
    res1: 'Faster decisions',
    res2: 'Better governance',
    res3: 'Increased clarity',
    ctaTitle: 'Lead with clarity. Govern with confidence.',
  },

  // Use Case - Public Sector
  ucPublic: {
    seoTitle: 'Public Sector KPI & Performance Management Software',
    seoDesc: 'Track public sector KPIs and projects with transparency. Improve accountability and reporting.',
    badge: 'Public Sector',
    heroTitle: 'Increase transparency and accountability.',
    heroDesc: 'Public organizations require clear reporting and measurable outcomes. Outsprinter provides a structured system for performance tracking.',
    challengeTitle: 'The Challenge',
    challengeIntro: 'Public sector organizations face:',
    ch1: 'Complex reporting',
    ch2: 'Low transparency',
    ch3: 'Slow processes',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, public organizations get:',
    how1: 'KPI tracking',
    how2: 'Project visibility',
    how3: 'Clear ownership',
    resultsTitle: 'Outcomes',
    res1: 'Better accountability',
    res2: 'Improved reporting',
    res3: 'Transparent performance',
    ctaTitle: 'Bring clarity and accountability to public service.',
  },

  // Use Case - Nonprofits
  ucNonprofit: {
    seoTitle: 'Nonprofit KPI & Impact Tracking Software',
    seoDesc: 'Track impact, manage projects, and improve transparency. Help your nonprofit deliver measurable results.',
    badge: 'Nonprofits',
    heroTitle: 'Measure impact. Build trust. Deliver results.',
    heroDesc: 'Nonprofits must demonstrate impact while managing limited resources. Outsprinter helps track outcomes and align execution.',
    challengeTitle: 'The Challenge',
    challengeIntro: 'Nonprofits typically face:',
    ch1: 'Hard-to-measure impact',
    ch2: 'Donor expectations',
    ch3: 'Manual reporting',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, nonprofits can:',
    how1: 'Track impact KPIs',
    how2: 'Manage programs as projects',
    how3: 'Provide transparent dashboards',
    resultsTitle: 'Outcomes',
    res1: 'Clear impact',
    res2: 'Better trust',
    res3: 'Improved focus',
    ctaTitle: 'Deliver more impact with clarity.',
  },

  // Use Case - Consulting & Agencies
  ucConsult: {
    seoTitle: 'Agency Project & KPI Management Software',
    seoDesc: 'Manage client projects, track KPIs, and improve delivery. One system for consulting teams and agencies.',
    badge: 'Consulting & Agencies',
    heroTitle: 'Deliver client results with clarity and control.',
    heroDesc: 'Agencies manage multiple clients, projects, and deadlines — often across many tools. Outsprinter brings everything into one system.',
    challengeTitle: 'The Challenge',
    challengeIntro: 'Agencies and consulting teams face:',
    ch1: 'Multiple clients',
    ch2: 'Task overload',
    ch3: 'Lack of visibility',
    howTitle: 'How Outsprinter Helps',
    howIntro: 'With Outsprinter, agencies get:',
    how1: 'Project-based structure per client',
    how2: 'KPI tracking for performance',
    how3: 'Task management for execution',
    resultsTitle: 'Outcomes',
    res1: 'Better delivery',
    res2: 'Happier clients',
    res3: 'Predictable results',
    ctaTitle: 'Deliver client results with confidence.',
  },

  // Use Case - Manufacturing
  ucManuf: {
    seoTitle: 'Production & Manufacturing KPI Software',
    seoDesc: 'Track production KPIs, manage operational tasks, and run improvement projects. Gain real-time visibility across your manufacturing operations.',
    badge: 'Production & Manufacturing',
    heroTitle: 'Increase efficiency. Reduce waste. Gain full control over operations.',
    heroDesc: 'Manufacturing companies operate in environments where small inefficiencies quickly turn into major costs. Production delays, quality issues, and misalignment between departments often stem from one core problem: lack of real-time visibility and structured execution. Outsprinter gives production companies a unified system to manage KPIs, projects, and daily operational tasks — ensuring that every part of the organization is aligned and continuously improving.',
    challengeTitle: 'The Challenge for Production Companies',
    challengeIntro: 'Manufacturing organizations typically struggle with:',
    ch1: 'KPIs tracked in spreadsheets or disconnected systems',
    ch2: 'Delays and issues discovered too late',
    ch3: 'Poor visibility across production lines, shifts, or plants',
    ch4: 'Too many metrics without clear focus',
    ch5: 'Teams reacting to problems instead of preventing them',
    challengeConclusion: 'As complexity increases, control decreases.',
    howTitle: 'How Outsprinter Brings Structure to Production',
    howIntro: 'Outsprinter connects production performance with daily execution. With Outsprinter, manufacturing companies can:',
    how1: 'Define and track production KPIs (OEE, downtime, scrap rate, throughput)',
    how2: 'Connect KPIs directly to projects and tasks',
    how3: 'Manage improvement initiatives as structured projects',
    how4: 'Assign clear ownership across production, maintenance, and quality teams',
    how5: 'Detect issues early with real-time dashboards and alerts',
    howConclusion: 'Execution becomes measurable, visible, and proactive.',
    workflowTitle: 'Typical Production Workflow in Outsprinter',
    wf1Title: 'Production Setup',
    wf1a: 'Leadership defines company-level KPIs',
    wf1b: 'Plants and departments define operational KPIs',
    wf1c: 'KPI owners assigned per line, shift, or function',
    wf2Title: 'Daily & Weekly Execution',
    wf2a: 'Teams manage tasks (maintenance, quality checks, improvements)',
    wf2b: 'Recurring tasks ensure operational routines (inspections, reports)',
    wf2c: 'Projects track improvement initiatives (efficiency, cost reduction)',
    wf2d: 'KPIs updated weekly (or integrated via import)',
    wf3Title: 'Management Review',
    wf3a: 'Leadership monitors dashboards across plants',
    wf3b: 'Issues (red KPIs) trigger immediate action',
    wf3c: 'AI highlights anomalies and performance drops',
    featuresTitle: 'Key Features for Production Companies',
    feat1Title: 'KPI Management',
    feat1a: 'Track production KPIs in one system',
    feat1b: 'Automatic status indicators (green / yellow / red)',
    feat1c: 'Trend visibility across time',
    feat2Title: 'Task Management',
    feat2a: 'Assign operational tasks with deadlines and priorities',
    feat2b: 'Subtasks for complex workflows',
    feat2c: 'Recurring tasks for routine operations',
    feat3Title: 'Project Management',
    feat3a: 'Manage improvement initiatives as projects',
    feat3b: 'Track progress and project health',
    feat3c: 'Monitor workload and resource allocation',
    feat4Title: 'Dashboards',
    feat4a: 'Real-time production performance overview',
    feat4b: 'Cross-department visibility',
    feat4c: 'Plant-level and company-level insights',
    feat5Title: 'Smart AI Assistant',
    feat5a: 'Detect performance anomalies',
    feat5b: 'Identify bottlenecks and risks',
    feat5c: 'Suggest improvement areas',
    resultsTitle: 'Measurable Outcomes for Manufacturing',
    resultsIntro: 'Production companies using Outsprinter typically achieve:',
    res1: 'Higher production efficiency',
    res2: 'Reduced downtime and waste',
    res3: 'Faster detection of issues',
    res4: 'Better coordination between teams',
    res5: 'More predictable output and delivery',
    resultsConclusion: 'Outsprinter helps manufacturing teams move from reactive firefighting to structured, continuous improvement.',
    whyTitle: 'Why Outsprinter Fits Manufacturing Environments',
    why1: 'Combines KPIs, tasks, and projects in one system',
    why2: 'Supports both daily operations and long-term improvements',
    why3: 'Scales from single plant to multi-site organizations',
    why4: 'Provides real-time visibility without adding complexity',
    ctaTitle: 'Run production with clarity and control.',
  },

  // Blog Post UI
  blogPost: {
    home: 'Home',
    backToBlog: 'Back to Blog',
    linkCopied: 'Link copied!',
    linkCopiedDesc: 'The article link has been copied to your clipboard.',
    topicsCovered: 'Topics covered:',
    shareArticle: 'Share this article:',
    aboutAuthor: 'About {{name}}',
    authorBio: 'Entrepreneur, angel investor, and founder of Outsprinter. Passionate about building scalable businesses with clear execution and measurable results.',
    connectLinkedIn: 'Connect on LinkedIn',
    relatedArticles: 'Related Articles',
    ctaTitle: 'Start Tracking KPIs the Right Way',
    ctaDesc: 'Put these insights into practice with Outsprinter. Weekly tracking, visual dashboards, and clear accountability.',
    ctaBtn: 'Create account',
    allRights: 'All rights reserved.',
  },

  cookies: {
    message: 'We use cookies and analytics to improve your experience. By accepting, you allow us to collect anonymous usage data via Google Analytics.',
    learnMore: 'Learn more',
    decline: 'Decline',
    accept: 'Accept',
  },
  strategyAnalyzer: {
    title: 'AI Strategy Analyzer',
    subtitle: 'Upload your strategy document and AI will suggest teams & KPIs',
    uploadFile: 'Upload a file (.txt, .csv, .pdf)',
    chooseFile: 'Choose file...',
    orPasteText: 'or paste text',
    textLabel: 'Strategy document text',
    textPlaceholder: 'Paste your company strategy, vision, goals, or business plan here...\n\nInclude details about your organization structure, key objectives, target markets, and performance goals.',
    charCount: 'characters',
    moreDetailBetter: 'The more detail you provide, the better the suggestions',
    skipStep: 'Skip this step',
    analyzeWithAI: 'Analyze with AI',
    textTooShort: 'Text too short',
    textTooShortDesc: 'Please provide more details about your company strategy (at least a few paragraphs).',
    provideText: 'Please provide strategy text',
    analyzingTitle: 'Analyzing your strategy...',
    analyzingDesc: 'AI is reading your document and preparing team & KPI suggestions.\nThis may take 15-30 seconds.',
    applyingTitle: 'Setting up your workspace...',
    reviewTitle: 'Review AI Suggestions',
    reviewSubtitle: 'Click on any item to include or exclude it',
    suggestedTeams: 'Suggested Teams',
    suggestedKPIs: 'Suggested KPIs',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    included: 'Included',
    excluded: 'Excluded',
    target: 'Target:',
    back: 'Back',
    apply: 'Apply {{teams}} teams & {{kpis}} KPIs',
    selectAtLeastOneTeam: 'Select at least one team to continue',
    selectOneTeamToast: 'Please select at least one team',
    analysisFailed: 'Analysis failed',
    tryAgain: 'Please try again.',
    applyError: 'Error applying suggestions',
    failedReadPDF: 'Failed to read PDF',
    failedReadPDFDesc: 'The PDF file could not be parsed. Try pasting the text directly.',
    unsupportedFile: 'Unsupported file type',
    unsupportedFileDesc: 'Please upload a .txt, .csv, or .pdf file, or paste your strategy text directly.',
  },
  sprintTasks: {
    tasks: 'Tasks',
    loadingTasks: 'Loading tasks...',
    noTasksTitle: 'No Tasks Yet',
    noTasksDesc: 'Tasks will appear here once added to sprints.',
    allTasks: 'All Tasks',
    notStarted: 'Not Started',
    inProgress: 'In Progress',
    done: 'Done',
    overdue: 'Overdue',
    task: 'Task',
    sprint: 'Sprint',
    responsible: 'Responsible',
    status: 'Status',
    progress: 'Progress',
    dueDate: 'Due Date',
    completed: 'Completed',
    duration: 'Duration',
    carriedOver: 'Carried over',
    sameDay: 'Same day',
    day: 'day',
    days: 'days',
    daysOverdue: 'd overdue',
    daysLeft: 'd left',
  },
};

export default en;
